import {
  ADDICTION,
  AGE_0_5,
  AGE_6_12,
  AGE_13_17,
  AGE_18_65,
  ANXIETY,
  COGNITIVE_BEHAVIORAL_THERAPY,
  DEPRESSION,
  EMDR,
  FEMALE_GENDER,
  GRIEF_BEREAVEMENT,
  IFilterFormValues,
  INTERNAL_FAMILY_SYSTEMS,
  MALE_GENDER,
  MINDFULNESS,
  NON_BINARY_GENDER,
  PSYCHODYNAMIC,
  STRESS,
  TRAUMA
} from '@sondermind/data-access/public-facing/filter-form';
import { FilterFormKey } from './filter-form-key.enum';
import { GuidedQuestionId } from './guided-question-id.enum';
import { IGuidedQuestion } from './guided-question.interface';

/**
 * Filter-form dimensions backed by array controls. Selecting one or more chips
 * for these keys produces an array value; every other filter key is treated as
 * a scalar. Mirrors the array controls in `IFilterForm`.
 */
export const GUIDED_ARRAY_FILTER_KEYS: ReadonlySet<keyof IFilterFormValues> = new Set([
  'specialties',
  'modalities',
  'genders',
  'sessionFormats',
  'ages',
  'treatmentPreferences'
]);

/**
 * Scalar filter dimensions that surface as removable chips in the zero-results
 * state. `state` is deliberately excluded — it is required and not removable.
 * Ordered so the zero-results chips render deterministically.
 */
export const GUIDED_REMOVABLE_SCALAR_KEYS: ReadonlySet<keyof IFilterFormValues> = new Set([
  'insurance',
  'serviceType'
]);

/**
 * Ordered guided questionnaire. Phase 1 is frontend-only and static: the core
 * questions (concerns, gender) come first, then follow-ups (age, therapeutic
 * approach). Every chip is direct-apply (carries `formKey` + `values`).
 *
 * Note: insurance is intentionally omitted here for Phase 1 — it needs direct
 * insurance-applying chips (see MER-4946 open question) rather than the
 * agent-prompt chips find-my-fit uses today. The copy/chips are placeholders
 * pending final design.
 */
export const GUIDED_QUESTIONS: IGuidedQuestion[] = [
  {
    id: GuidedQuestionId.CONCERNS,
    title: 'What brought you here today?',
    subtitle: 'Pick anything you’d like support with.',
    chips: [
      { title: 'Anxiety or worry', values: [ANXIETY], formKey: FilterFormKey.SPECIALTIES },
      { title: 'Stress', values: [STRESS], formKey: FilterFormKey.SPECIALTIES },
      { title: 'Feeling down or depressed', values: [DEPRESSION], formKey: FilterFormKey.SPECIALTIES },
      { title: 'Past traumatic experiences', values: [TRAUMA], formKey: FilterFormKey.SPECIALTIES },
      { title: 'Grief', values: [GRIEF_BEREAVEMENT], formKey: FilterFormKey.SPECIALTIES },
      { title: 'Substance use', values: [ADDICTION], formKey: FilterFormKey.SPECIALTIES }
    ]
  },
  {
    id: GuidedQuestionId.GENDER,
    title: 'Do you have a gender preference for your provider?',
    chips: [
      { title: 'Female', values: [FEMALE_GENDER], formKey: FilterFormKey.GENDERS },
      { title: 'Male', values: [MALE_GENDER], formKey: FilterFormKey.GENDERS },
      { title: 'Non-binary', values: [NON_BINARY_GENDER], formKey: FilterFormKey.GENDERS }
    ]
  },
  {
    id: GuidedQuestionId.AGE,
    title: 'Who is this care for?',
    chips: [
      { title: 'Young children (0-5)', values: [AGE_0_5], formKey: FilterFormKey.AGES },
      { title: 'Children (6-12)', values: [AGE_6_12], formKey: FilterFormKey.AGES },
      { title: 'Teens (13-17)', values: [AGE_13_17], formKey: FilterFormKey.AGES },
      { title: 'Adults (18+)', values: [AGE_18_65], formKey: FilterFormKey.AGES }
    ]
  },
  {
    id: GuidedQuestionId.TREATMENT_APPROACH,
    title: 'Any therapeutic approach you prefer?',
    chips: [
      {
        title: COGNITIVE_BEHAVIORAL_THERAPY.title,
        values: [COGNITIVE_BEHAVIORAL_THERAPY],
        formKey: FilterFormKey.TREATMENT_PREFERENCES
      },
      { title: EMDR.title, values: [EMDR], formKey: FilterFormKey.TREATMENT_PREFERENCES },
      { title: MINDFULNESS.title, values: [MINDFULNESS], formKey: FilterFormKey.TREATMENT_PREFERENCES },
      {
        title: INTERNAL_FAMILY_SYSTEMS.title,
        values: [INTERNAL_FAMILY_SYSTEMS],
        formKey: FilterFormKey.TREATMENT_PREFERENCES
      },
      { title: PSYCHODYNAMIC.title, values: [PSYCHODYNAMIC], formKey: FilterFormKey.TREATMENT_PREFERENCES }
    ]
  }
];
